import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Routes } from '../../utils/Routes';

interface AssociatedArt {
  id: string;
  image: string;
  title: string;
}

interface MusicListProps {
  id: number;
  musicFileName: string;
  title?: string;
  artist?: string;
  isPlaying?: boolean;
  isSelected?: boolean;
  associatedArt?: AssociatedArt[];
  onPlay?: () => void;
  onPause?: () => void;
}

export default function MusicList({
  id,
  musicFileName,
  title = 'Untitled Soundtrack',
  artist = 'Unknown Artist',
  isPlaying = false,
  isSelected = false,
  associatedArt = [],
  onPlay,
  onPause,
}: MusicListProps) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLooping, setIsLooping] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    const handleEnded = () => {
      if (!isLooping) {
        onPause?.();
      }
    };

    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('ended', handleEnded);

    if (isPlaying) {
      audioElement.play().catch((error) => {
        console.error('Audio playback failed:', error);
      });
    } else if (isSelected) {
      audioElement.pause();
    } else {
      audioElement.pause();
      audioElement.currentTime = 0;
      setCurrentTime(0);
      setIsLooping(false);
      audioElement.loop = false;
    }

    return () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('ended', handleEnded);
    };
  }, [isPlaying, isLooping, onPause, isSelected]);

  const togglePlayPause = () => {
    if (isPlaying) {
      onPause?.();
    } else {
      onPlay?.();
    }
  };

  const toggleLoop = () => {
    const audioElement = audioRef.current;
    if (audioElement) {
      const newLoopState = !audioElement.loop;
      audioElement.loop = newLoopState;
      setIsLooping(newLoopState);
    }
  };

  const handleProgressClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const audioElement = audioRef.current;
    if (audioElement && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const newTime = ((e.clientX - rect.left) / rect.width) * duration;
      audioElement.currentTime = Math.max(0, Math.min(newTime, duration));
    }
  };

  const formatTime = (time: number) => {
    if (!isFinite(time) || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div
      className={`group flex items-center gap-3 px-3 py-3 rounded-lg transition-colors duration-200 ${
        isSelected ? 'bg-gray-800/80' : 'hover:bg-gray-800/50'
      }`}
    >
      {/* Dot / Play Button */}
      <div className="w-8 flex justify-center items-center shrink-0">
        {!isSelected ? (
          <button
            onClick={togglePlayPause}
            aria-label="Play"
            className="text-gray-500 group-hover:text-custom-stone transition-colors duration-200"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-gray-500 block group-hover:hidden" />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 hidden group-hover:block"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </button>
        ) : (
          <button
            onClick={togglePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className="text-custom-stone hover:text-custom-stone/80 transition-colors duration-200"
          >
            {isPlaying ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium truncate transition-colors duration-200 ${
            isSelected
              ? 'text-custom-stone'
              : 'text-gray-300 group-hover:text-white'
          }`}
        >
          {title}
        </p>
        <p className="text-xs text-gray-500 truncate mt-0.5">{artist}</p>

        {/* Progress Bar */}
        {isSelected && (
          <div className="mt-2">
            <div
              className="w-full h-1 bg-gray-700 rounded-full cursor-pointer"
              onClick={handleProgressClick}
              role="progressbar"
              aria-valuenow={(currentTime / duration) * 100 || 0}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-1 bg-custom-stone rounded-full transition-all duration-100"
                style={{
                  width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                }}
              />
            </div>
            <span className="text-xs text-gray-500 mt-1 block tabular-nums">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        )}
      </div>

      {/* Associated Paintings */}
      {associatedArt.length > 0 && (
        <div className="flex items-center gap-1 shrink-0">
          {associatedArt.slice(0, 3).map((art) => (
            <Link
              key={art.id}
              to={`${Routes.Art}/${art.id}`}
              title={art.title}
              className="relative group/art block"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={art.image}
                alt={art.title}
                className={`w-8 h-8 rounded object-cover transition-all duration-300 hover:opacity-100 ${
                  isPlaying
                    ? 'opacity-100 border border-custom-stone/70 shadow-[0_0_6px_rgba(209,191,167,0.4)]'
                    : 'opacity-40 border border-gray-700 hover:border-custom-stone/50'
                }`}
              />
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover/art:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                {art.title}
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Sound Bars */}
      {isSelected && (
        <div className="flex items-center gap-0.5 shrink-0">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="w-1 h-4 bg-custom-stone/70 rounded-full"
              style={{
                animation: `pulse ${0.5 + i * 0.1}s ease-in-out infinite`,
                animationPlayState: isPlaying ? 'running' : 'paused',
              }}
            />
          ))}
        </div>
      )}

      {/* Loop Button */}
      {isSelected && (
        <div className="relative group/loop shrink-0">
          <button
            onClick={toggleLoop}
            aria-label={isLooping ? 'Loop On' : 'Loop Off'}
            aria-pressed={isLooping}
            className={`transition-colors duration-200 active:scale-90 ${
              isLooping
                ? 'text-custom-stone hover:text-custom-stone/80'
                : 'text-gray-600 hover:text-gray-400'
            }`}
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 32 25"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                d="M23.5 16C22 20 18 23 14 23C8.5 23 4 19 3.3 13.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 10C5.5 6 9 2.5 14 2.5C19.5 2.5 24 7 24.5 12"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M24.5 23L24.5 17.5C24.5 17.2 24.2 17 23.9 17H17"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 10.5H4C3.7 10.5 3.5 10.2 3.5 9.5V4"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover/loop:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
            {isLooping ? 'Loop On' : 'Loop Off'}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800" />
          </div>
        </div>
      )}

      <audio
        ref={audioRef}
        src={`/assets/music/${musicFileName}`}
        preload="metadata"
      />
    </div>
  );
}
